/* eslint-disable max-classes-per-file */
import { MainTableFilterModel } from 'src/app/shared/models/main-table-filter.model';
import { IListDocuments } from 'src/app/document-admin/models/list-documents.model';
import { IDocumentRequest, IReadReceiptRequest } from '../models/document.model';
import { IDepartmentReadReceiptSetting } from '../models/read-receipt-settings.model';
import { DocumentStatus } from '../enums/document-status.enum';
import { DocumentAvailability } from '../enums/document-availability.enum';
import { DocumentPrintingLogoType } from '../enums/document-printing-logo-type.enum';
import { IDocumentVersionDisambiguator } from '../functions/document-version-resolution';
import { IDocumentPrintOptions } from '../models/document-print-options.model';

export class LoadDocuments {
  static readonly type = '[Documents] Load Documents via Service';
  constructor(
    public filter?: MainTableFilterModel,
    public query?: IListDocuments
  ) {}
}

export class LoadDocumentsByMenu {
  static readonly type = '[Documents] Load Documents By Menu via Service';
  constructor(public filter?: MainTableFilterModel) {}
}

export class LoadDocumentsAwaitingReadReceipt {
  static readonly type = '[Documents] Load Documents Awaiting Read Receipt';
  constructor(public filter?: MainTableFilterModel) {}
}

export class LoadAdminDocumentReadReceiptRequests {
  static readonly type = '[Document Read Receipts] Load Admin Read Receipt Requests';
  constructor(public filter?: MainTableFilterModel) {}
}

export class LoadUserDocumentReadReceipts {
  static readonly type = '[Document Read Receipts] Load User Document Read Receipts';
  constructor(
    public userId: string,
    public filter?: MainTableFilterModel
  ) {}
}

export class LoadDocumentById {
  static readonly type = '[Document] Load a single document';
  constructor(
    public documentId: string,
    public includeDeleted?: boolean,
    public forcedVersionId?: string
  ) {}
}

export class LoadDocumentsForIO {
  static readonly type = '[Documents] Load Documents for IO via Service';
  constructor() {}
}

export class CreateDocument {
  static readonly type = '[Document] Create document';
  constructor(public document: IDocumentRequest) {}
}

export class UpdateDocument {
  static readonly type = '[Document] Update document';
  constructor(public document: IDocumentRequest) {}
}

export class DeleteDocument {
  static readonly type = '[Document] Delete document';
  constructor(
    public documentId: string,
    public status: DocumentStatus
  ) {}
}

export class LoadDocumentVersions {
  static readonly type = '[Document] Load a single document versions';
  constructor(
    public documentId: string,
    public includeDeleted?: boolean
  ) {}
}

export class LoadDocumentVersion {
  static readonly type = `[Document] Load document's single version`;
  constructor(
    public documentId: string,
    public documentVersionId: string,
    public includeDeleted?: boolean,
    public versionDisambiguator?: IDocumentVersionDisambiguator
  ) {}
}

export class PrintDocumentTree {
  static readonly type = `[Document] Print document tree`;
  constructor(
    public menuId: string,
    public tableOfContents: boolean,
    public printLogoType: DocumentPrintingLogoType,
    public useLargeTenantLogo: boolean,
    public includeDocumentId: boolean,
    public isLandscape: boolean,
    public useNewPdfService: boolean,
    public filter: MainTableFilterModel,
    public statuses?: (DocumentStatus | undefined)[],
    public documentAvailability?: DocumentAvailability
  ) {}
}

export class PrintDocument {
  static readonly type = `[Document] Print single document`;
  constructor(public options: IDocumentPrintOptions) {}
}

export class PrintTextControlOnly {
  static readonly type = `[Document] Print Text Control only`;
  constructor(
    public documentId: string,
    public departmentId: string
  ) {}
}

export class ApproveDocument {
  static readonly type = `[Document] Approve Document`;
  constructor(
    public documentId: string,
    public menuId: string,
    public approvalStageId?: string,
    public comment?: string,
    public readReceipt?: IReadReceiptRequest
  ) {}
}

export class RejectDocument {
  static readonly type = `[Document] Reject Document`;
  constructor(
    public documentId: string,
    public menuId: string,
    public approvalStageId?: string,
    public comment?: string
  ) {}
}

export class EnableDocument {
  static readonly type = `[Document] Enable Document`;
  constructor(
    public documentId: string,
    public menuId: string
  ) {}
}

export class DisableDocument {
  static readonly type = `[Document] Disable Document`;
  constructor(
    public documentId: string,
    public menuId: string
  ) {}
}

export class RestoreDocument {
  static readonly type = `[Document] Restore Document`;
  constructor(
    public documentId: string,
    public menuId: string
  ) {}
}

export class LoadCurrentDepartmentReadReceiptSettings {
  static readonly type = `[Document] Get Current Department Read Receipt Settings`;
  constructor() {}
}

export class UpdateDepartmentsReadReceiptSettings {
  static readonly type = `[Document] Update Departments Read Receipt Settings`;
  constructor(public settings: IDepartmentReadReceiptSetting[]) {}
}

export class ConfirmDocumentRead {
  static readonly type = `[Document] Confirm Document Read`;
  constructor(public documentId: string) {}
}

export class UpdateDocumentReadReceipt {
  static readonly type = `[Document] Update Document Read Receipt`;
  constructor(
    public documentId: string,
    public readReceiptId: string,
    public readReceipt: IReadReceiptRequest
  ) {}
}

export class LoadDocumentReadReceipts {
  static readonly type = `[Document] Load Document Read Receipts`;
  constructor(public documentId: string) {}
}

export class CancelDocumentReadReceiptRequest {
  static readonly type = `[Document] Cancel Document Read Receipt Request`;
  constructor(
    public documentId: string,
    public requestId: string
  ) {}
}

export class DeleteDocumentReadReceipt {
  static readonly type = `[Document] Delete Document Read Receipt`;
  constructor(
    public documentId: string,
    public readReceiptId: string
  ) {}
}

export class RequestChangeOnDocument {
  static readonly type = `[Document] Create Document Change Request`;
  constructor(
    public documentId: string,
    public description: string,
    public menuId?: string
  ) {}
}

export class AcceptDocumentChangeRequest {
  static readonly type = `[Document] Accept Document Change Request`;
  constructor(
    public documentId: string,
    public reviewComment: string,
    public id?: string,
    public menuId?: string
  ) {}
}

export class RejectDocumentChangeRequest {
  static readonly type = `[Document] Reject Document Change Request`;
  constructor(
    public documentId: string,
    public reviewComment: string,
    public id?: string,
    public menuId?: string
  ) {}
}

export class LoadDocumentCounts {
  static readonly type = `[Document] Load Document Counts`;
  constructor(public menuId?: string) {}
}

export class LoadDocumentNavigation {
  static readonly type = `[Document] Load document navigation`;
  constructor(
    public documentId: string,
    public filter?: MainTableFilterModel
  ) {}
}

export class ClearDocumentsByMenu {
  static readonly type = `[Document] Clear Documents By Menu`;
  constructor() {}
}

export class ClearCurrentDocument {
  static readonly type = `[Document] Clear Current Document`;
  constructor() {}
}

export class SetRevisionNumber {
  static readonly type = `[Document] Set Revision Number`;
  constructor(
    public documentId: string,
    public revisionNumber: number,
    public comment: string
  ) {}
}
